import React, { Component, ErrorInfo, ReactNode } from "react";
import { isAbortError } from "../../Utils/errors/isAbortError";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const CHUNK_RELOAD_KEY = "sb_chunk_load_reload_v1";

function isChunkLoadError(error: Error | null | undefined): boolean {
  if (!error) return false;
  const name = String((error as any)?.name || "");
  const msg = String(error?.message || error || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Abort/cancel races remend in data layers — never crash the page on them.
    if (isAbortError(error)) {
      return {
        hasError: false,
        error: null,
      };
    }
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isAbortError(error)) {
      return;
    }

    // Log error to console for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Stale deploy: old HTML requests a hashed chunk that 404s after a new
    // Vercel build. One hard reload usually picks up the new asset map.
    if (isChunkLoadError(error)) {
      try {
        const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
        if (!alreadyReloaded) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
          window.location.reload();
          return;
        }
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch (_e) {
        // fall through to fallback UI
      }
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI or default
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const chunkMiss = isChunkLoadError(this.state.error);

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <h2 style={{ color: "#333", marginBottom: "16px" }}>
            Something went wrong
          </h2>
          <p style={{ color: "#666", marginBottom: "24px" }}>
            {chunkMiss
              ? "This page was updated while you were onboarding. Refresh to load the latest version."
              : "We're sorry, but something unexpected happened. Please try refreshing the page."}
          </p>
          {!chunkMiss ? (
            <button
              onClick={this.handleReset}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Try Again
            </button>
          ) : null}
          <button
            onClick={() => {
              try {
                sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              } catch (_e) {
                // ignore
              }
              window.location.reload();
            }}
            style={{
              padding: "10px 20px",
              backgroundColor: chunkMiss ? "#007bff" : "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              marginLeft: chunkMiss ? "0" : "10px",
            }}
          >
            Refresh Page
          </button>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <details
              style={{
                marginTop: "24px",
                padding: "16px",
                backgroundColor: "#fff",
                borderRadius: "4px",
                maxWidth: "800px",
                textAlign: "left",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: "bold" }}>
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  marginTop: "12px",
                  overflow: "auto",
                  fontSize: "12px",
                  color: "#d32f2f",
                }}
              >
                {this.state.error.toString()}
                {this.state.error.stack && (
                  <div style={{ marginTop: "8px" }}>
                    {this.state.error.stack}
                  </div>
                )}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
